import { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie'; // Import useCookies hook
import Logo from '../../components/logo/Logo';
import Button from '../../components/buttons/Button';
import ApiService from '../../services/ApiService';
import OtpInputPage from './OtpInput';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import ReCAPTCHA from 'react-google-recaptcha';
import { setCardDetails } from '../../store/slices/authDetails';
import { setMerchants, setSelectedMerchant } from '@store/slices/merchantSlice';
import { getMerchantsWithLoginFallback } from '@src/utils/merchantHelpers';
import { getErrorMessage, getErrorStatus } from '@src/utils/errorMessage';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

const Login = () => {
  const dispatch = useDispatch();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const { handleLogin, handleLogout, isAuthenticated } = useAuth();
  const [recaptchaVal, setRecaptchaVal] = useState<null | string>(null);
  const [, setCookie] = useCookies([
    'authToken',
    'refreshToken',
    'loungeId',
    'loungeDetails',
    'role',
  ]);
  const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  const togglePasswordViewHandler = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleCaptchaVerification = (value: string | null) => {
    if (value) {
      setRecaptchaVal(value);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setLoading(true);
      const response = await ApiService.post('v1/user/staff/login', {
        username: username,
        password: password,
        captchaToken: recaptchaVal,
      });

      const payload = response.data ?? response;
      if (payload.access_token) {
        const { access_token, refresh_token, member_details } = payload;

        dispatch(setCardDetails(member_details));

        // Set cookies for session data
        setCookie('authToken', access_token, {
          path: '/',
          secure: true, // Ensure secure cookies in production
          sameSite: 'strict',
        });
        setCookie('refreshToken', refresh_token, {
          path: '/',
          secure: true,
          sameSite: 'strict',
        });
        setCookie('loungeId', member_details?.lounge_id, {
          path: '/',
          secure: true,
          sameSite: 'strict',
        });
        setCookie('loungeDetails', JSON.stringify(member_details?.lounge), {
          path: '/',
          secure: true,
          sameSite: 'strict',
        });
        setCookie('role', member_details?.role, {
          path: '/',
          secure: true,
          sameSite: 'strict',
        });

        // Primary: use merchants from login response; fallback: call merchant API
        setLoadingMerchants(true);
        const { merchants, error } = await getMerchantsWithLoginFallback(
          member_details?.merchants as
            | import('@src/types/merchant').Merchant[]
            | undefined,
        );
        setLoadingMerchants(false);

        if (!merchants.length) {
          if (error) {
            const status = getErrorStatus(error);
            if (status === 403) {
              toast.error(
                'Access Denied: You do not have permission to access any merchant. Please contact your administrator.',
                { autoClose: 5000 },
              );
            } else if (status === 401) {
              toast.error('Authentication failed. Please log in again.');
            } else {
              toast.error(
                getErrorMessage(
                  error,
                  'Failed to load merchants after multiple attempts.',
                ),
                { autoClose: 5000 },
              );
            }
          } else {
            toast.error(
              'No active merchants found for your account. Please contact support.',
              { autoClose: 5000 },
            );
          }
          setLoading(false);
          handleLogout();
          return;
        }

        dispatch(setMerchants(merchants));
        dispatch(setSelectedMerchant(merchants[0]));

        handleLogin();
        navigate('/', { state: { ...payload } });
      } else {
        setLoading(false);
        setStep(2);
      }
    } catch (error: unknown) {
      setLoading(false);
      setLoadingMerchants(false);
      const msg = getErrorMessage(error);
      setError(msg);
      toast.error(msg, { autoClose: 5000 });
      console.error('Login error:', error);
    }
  };

  const handleReturn = () => {
    setStep(0);
    setUsername('');
    setPassword('');
    setError('');
    setIsPasswordVisible(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="bg-lightBlue flex h-screen w-full justify-center overflow-y-auto p-6 lg:p-10 xl:p-20">
      <div className="flex w-[602px] flex-col gap-4 xl:gap-8 2xl:gap-[60px]">
        <Logo />
        {step === 0 && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <div className="text-center text-2xl font-semibold leading-[45px] text-[#1E429F] xl:text-3xl">
              Login credentials
            </div>
            <div className="flex items-center justify-center gap-[16px] rounded-3xl bg-white px-12 py-10 shadow-md 2xl:py-[63px]">
              <div className="space-y-6 md:w-[506px]">
                <div>
                  <label
                    htmlFor="username"
                    className="block text-justify text-lg font-medium leading-6 text-gray-900"
                  >
                    Username
                  </label>
                  <div className="mt-2">
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      className="block w-full rounded-[8px] border-0 bg-[#F9FAFB] px-4 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-[#D1D5DB] placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-inset sm:text-lg sm:leading-10"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="password"
                      className="block text-lg font-medium leading-6 text-gray-900"
                    >
                      Password
                    </label>
                  </div>
                  <div className="relative mt-2">
                    <input
                      id="password"
                      name="password"
                      type={isPasswordVisible ? 'text' : 'password'}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="block w-full rounded-[8px] border-0 bg-[#F9FAFB] px-4 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-[#D1D5DB] placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-inset sm:text-lg sm:leading-10"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordViewHandler}
                      className="absolute right-1 top-1 flex h-[90%] w-[40px] items-center justify-center bg-[#F9FAFB]"
                    >
                      <svg
                        fill="#323232"
                        width="28px"
                        height="28px"
                        viewBox="0 0 0.54 0.54"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M0.493 0.261C0.448 0.155 0.362 0.09 0.27 0.09s-0.178 0.065 -0.223 0.171a0.022 0.022 0 0 0 0 0.018C0.092 0.385 0.178 0.45 0.27 0.45s0.178 -0.065 0.223 -0.171a0.022 0.022 0 0 0 0 -0.018M0.27 0.405c-0.071 0 -0.139 -0.052 -0.178 -0.135C0.131 0.187 0.199 0.135 0.27 0.135s0.139 0.052 0.178 0.135c-0.039 0.083 -0.106 0.135 -0.178 0.135m0 -0.225a0.09 0.09 0 1 0 0.09 0.09 0.09 0.09 0 0 0 -0.09 -0.09m0 0.135a0.045 0.045 0 1 1 0.045 -0.045 0.045 0.045 0 0 1 -0.045 0.045" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-4">
                  <ReCAPTCHA
                    sitekey={RECAPTCHA_SITE_KEY || ''}
                    onChange={handleCaptchaVerification}
                  />
                  <p className="text-sm text-gray-500">
                    Please complete the CAPTCHA verification to continue
                  </p>
                </div>
                {/* {error && (
                  <p className="text-red-500 font-medium mb-4">{error}</p>
                )} */}
                <Button
                  disabled={
                    password.length <= 0 ||
                    username.length <= 0 ||
                    !recaptchaVal
                  }
                  buttonType="submit"
                  text="Continue"
                  className="!text-lg"
                  loading={loading || loadingMerchants}
                />
              </div>
            </div>
          </form>
        )}
        {step === 2 && (
          <OtpInputPage
            username={username}
            password={password}
            onBackToLogin={handleReturn}
          ></OtpInputPage>
        )}
      </div>
    </div>
  );
};

export default Login;
